import { useMutation } from "@tanstack/react-query";
import {
  Archive,
  CalendarClock,
  Check,
  Send,
  Undo2,
} from "lucide-react";
import { useState } from "react";
import { Button, FormField, Input, Textarea } from "@/components/ui/controls";
import { Dialog } from "@/components/ui/overlays";
import { useToast } from "@/components/ui/toast-context";
import { ApiError, apiRequest, jsonBody } from "@/lib/api/client";
import { toWorkflow } from "@/lib/adapters/content";
import type { Workflow } from "@/lib/domain/models";
import type { WorkflowActionContext } from "@/lib/domain/workflow-queue";
import { workflowSchema } from "@/lib/schemas/common";
import {
  footerWorkflowActions,
  primaryWorkflowActions,
} from "./workflow-actions";
import styles from "./workflow-controls.module.css";

const labels: Record<string, string> = {
  submit: "Onaya gönder",
  approve: "Onayla",
  request_changes: "Değişiklik iste",
  withdraw_change_request: "Değişiklik isteğini geri al",
  publish: "Yayınla",
  schedule: "Zamanla",
  archive: "Arşivle",
};

const successMessages: Record<string, string> = {
  submit: "İçerik onaya gönderildi",
  approve: "İçerik onaylandı",
  request_changes: "Değişiklik isteği gönderildi",
  withdraw_change_request: "Değişiklik isteği geri alındı",
  publish: "İçerik yayınlandı",
  schedule: "Yayın zamanlandı",
  archive: "İçerik arşivlendi",
};

function actionLabel(action: string, state: string) {
  if (action === "submit" && state === "changes_requested") {
    return "Yeniden onaya gönder";
  }
  return labels[action] ?? action;
}

type ActionInput = WorkflowActionContext & {
  action: string;
  note?: string;
};

export function WorkflowControls({
  workflow,
  onUpdate,
  onError,
  compact = false,
  placement = "default",
  disabled = false,
  disabledReason,
  includeActions,
  excludeActions,
  immediateActions,
}: {
  workflow: Workflow;
  onUpdate: (workflow: Workflow, context: WorkflowActionContext) => void;
  onError: (message: string) => void;
  compact?: boolean;
  placement?: "default" | "footer";
  disabled?: boolean;
  disabledReason?: string;
  includeActions?: readonly string[];
  excludeActions?: readonly string[];
  immediateActions?: readonly string[];
}) {
  const notify = useToast();
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");
  const mutation = useMutation({
    mutationFn: async ({ action, note: actionNote, scheduledFor: date }: ActionInput) => {
      const payload: Record<string, unknown> = {
        action,
        version: workflow.version,
      };
      if (actionNote !== undefined) payload.note = actionNote;
      if (date) payload.scheduled_for = date;
      return apiRequest(`workflow/${workflow.id}/`, workflowSchema, {
        method: "POST",
        ...jsonBody(payload),
      });
    },
    onSuccess: (result, variables) => {
      onUpdate(toWorkflow(result.data), variables);
      setPendingAction(null);
      setNote("");
      setScheduledFor("");
      notify({
        title: successMessages[variables.action] ?? "İşlem tamamlandı",
        tone: "success",
      });
    },
    onError: (reason) => {
      const description =
        reason instanceof ApiError
          ? reason.message
          : reason instanceof Error
            ? reason.message
            : "İşlem tamamlanamadı.";
      onError(description);
      notify({ title: "İşlem tamamlanamadı", description, tone: "danger" });
    },
  });

  // The API is the workflow authority. The two editor-only commands are
  // rendered by their dedicated controls; every other allowed action is
  // presented without re-deriving state or permission rules in React.
  const actions = workflow.actions.filter(
    (action) =>
      action !== "save" &&
      action !== "restore" &&
      (!includeActions || includeActions.includes(action)) &&
      !excludeActions?.includes(action),
  );
  if (!workflow.id || actions.length === 0) return null;

  const actionDisabled = disabled || mutation.isPending;
  const footerActions = footerWorkflowActions(actions);

  function start(action: string) {
    onError("");
    if (immediateActions?.includes(action)) {
      mutation.mutate({ action });
      return;
    }
    if (
      [
        "request_changes",
        "withdraw_change_request",
        "schedule",
        "archive",
      ].includes(action)
    ) {
      setPendingAction(action);
      return;
    }
    mutation.mutate({ action });
  }

  function confirm() {
    if (!pendingAction) return;
    if (pendingAction === "request_changes" && !note.trim()) {
      onError("İstenen değişikliği açıklayın.");
      notify({
        title: "Değişiklik notu gerekli",
        description: "İstenen değişikliği açıklayın.",
        tone: "danger",
      });
      return;
    }
    if (pendingAction === "schedule" && !scheduledFor) {
      onError("Yayın tarihini ve saatini seçin.");
      notify({
        title: "Yayın zamanı gerekli",
        description: "Yayın tarihini ve saatini seçin.",
        tone: "danger",
      });
      return;
    }
    mutation.mutate({
      action: pendingAction,
      note,
      scheduledFor,
    });
  }

  function actionIcon(action: string) {
    if (action === "submit") return <Send size={16} />;
    if (action === "approve") return <Check size={16} />;
    if (action === "schedule") return <CalendarClock size={16} />;
    if (action === "archive") return <Archive size={16} />;
    if (action === "request_changes") return <Undo2 size={16} />;
    if (action === "withdraw_change_request") return <Undo2 size={16} />;
    return null;
  }

  function actionButton(action: string, className?: string) {
    return (
      <Button
        type="button"
        size={compact ? "sm" : "md"}
        variant={
          action === "archive" && placement !== "footer"
            ? "danger"
            : primaryWorkflowActions.has(action)
              ? "primary"
              : "secondary"
        }
        className={className}
        disabled={actionDisabled}
        title={actionDisabled ? disabledReason : undefined}
        key={action}
        onClick={() => start(action)}
      >
        {actionIcon(action)}
        {actionLabel(action, workflow.state)}
      </Button>
    );
  }

  return (
    <>
      <div className={styles.root} data-compact={compact}>
        {!compact && placement !== "footer" ? <strong className={styles.title}>Yayın akışı</strong> : null}
        <div className={styles.actions} data-placement={placement}>
          {placement === "footer"
            ? footerActions.map((action) =>
                actionButton(
                  action,
                  primaryWorkflowActions.has(action)
                    ? styles.primaryAction
                    : action === "archive"
                      ? styles.archiveAction
                      : styles.secondaryAction,
                ),
              )
            : actions.map((action) => actionButton(action))}
        </div>
      </div>
      <Dialog
        open={Boolean(pendingAction)}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title={pendingAction ? actionLabel(pendingAction, workflow.state) : "Yayın işlemi"}
        description={
          pendingAction === "request_changes"
            ? "Düzeltilmesi gereken noktaları açıkça belirtin."
            : pendingAction === "withdraw_change_request"
              ? "Değişiklik isteği geçmişte korunur ve içerik, değiştirilmeden yeniden onay bekleyen duruma alınır."
            : pendingAction === "schedule"
              ? "İçeriğin otomatik yayınlanacağı zamanı seçin."
              : "Kayıt siteden kaldırılır, yayın geçmişi korunur."
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingAction(null)}>Vazgeç</Button>
            <Button
              variant={pendingAction === "archive" ? "danger" : "primary"}
              onClick={confirm}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "İşleniyor…" : "İşlemi tamamla"}
            </Button>
          </>
        }
      >
        {pendingAction === "schedule" ? (
          <FormField label="Yayın tarihi ve saati" htmlFor={`schedule-${workflow.id}`} required>
            <Input
              id={`schedule-${workflow.id}`}
              type="datetime-local"
              value={scheduledFor}
              onChange={(event) => setScheduledFor(event.target.value)}
            />
          </FormField>
        ) : pendingAction === "request_changes" || pendingAction === "archive" ? (
          <FormField
            label={pendingAction === "request_changes" ? "Değişiklik notu" : "Arşivleme notu"}
            htmlFor={`workflow-note-${workflow.id}`}
            required={pendingAction === "request_changes"}
          >
            <Textarea
              id={`workflow-note-${workflow.id}`}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              required={pendingAction === "request_changes"}
              rows={5}
            />
          </FormField>
        ) : null}
      </Dialog>
    </>
  );
}
